import { Box, Card, Typography, alpha } from '@mui/material';
import { ActionCard } from 'components/action-card/action-card';
import { addCommas } from '@persian-tools/persian-tools';
import React from 'react';

const iranSans = 'IRANSans, Tahoma, Arial, sans-serif';

const GatewatActionCardItem: React.FC<{
    icon: JSX.Element;
    color: string;
    background?: string;
    title: string;
    amount: number;
    count: number;
    periodHint: string;
}> = ({ icon, color, background, title, amount, count, periodHint }) => {
    return (
        <Card
            elevation={0}
            sx={(theme) => ({
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                minHeight: 154,
                p: { xs: 1.65, md: 1.85 },
                borderRadius: '18px',
                border: 0,
                background: `linear-gradient(145deg, ${alpha(
                    color,
                    theme.palette.mode === 'dark' ? 0.075 : 0.05,
                )} 0%, transparent 48%), ${theme.palette.background.paper}`,
                boxShadow:
                    theme.palette.mode === 'light'
                        ? '0 12px 36px rgba(17, 24, 39, 0.045)'
                        : `0 10px 30px ${alpha(theme.palette.common.black, 0.12)}`,
                transition:
                    'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow:
                        theme.palette.mode === 'light'
                            ? '0 18px 42px rgba(17, 24, 39, 0.075)'
                            : `0 16px 36px ${alpha(theme.palette.common.black, 0.2)}`,
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    insetInlineStart: 0,
                    top: 22,
                    width: 3,
                    height: 34,
                    borderRadius: '0 4px 4px 0',
                    bgcolor: color,
                    opacity: 0.9,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: 112,
                    height: 112,
                    borderRadius: '50%',
                    insetInlineEnd: -48,
                    top: -54,
                    background: alpha(
                        color,
                        theme.palette.mode === 'dark' ? 0.07 : 0.045,
                    ),
                    filter: 'blur(1px)',
                    pointerEvents: 'none',
                },
            })}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    position: 'relative',
                    zIndex: 1,
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.05,
                        minWidth: 0,
                    }}>
                    <ActionCard
                        width={40}
                        height={40}
                        icon={icon}
                        color={color}
                        background={background}
                        fontSize={17}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            className="font-yekan-heavy"
                            sx={{
                                fontSize: 13.25,
                                color: 'text.primary',
                                lineHeight: 1.7,
                            }}>
                            {title}
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.05,
                                fontSize: 10.25,
                                color: 'text.secondary',
                                lineHeight: 1.6,
                            }}>
                            {periodHint}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mt: 1.45, position: 'relative', zIndex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        gap: 0.65,
                    }}>
                    <Typography
                        sx={{
                            fontFamily: `${iranSans} !important`,
                            fontSize: { xs: 20, md: 22 },
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.4,
                            letterSpacing: '-0.2px',
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                        {addCommas(Number(amount || 0))}
                    </Typography>
                    <Typography sx={{ fontSize: 11.25, color: 'text.secondary' }}>
                        ریال
                    </Typography>
                </Box>

                <Box
                    sx={(theme) => ({
                        mt: 1.15,
                        px: 1,
                        py: 0.72,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.65,
                        bgcolor: alpha(
                            theme.palette.success.main,
                            theme.palette.mode === 'dark' ? 0.055 : 0.04,
                        ),
                    })}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            boxShadow: (theme) =>
                                `0 0 0 3px ${alpha(
                                    theme.palette.success.main,
                                    0.1,
                                )}`,
                            flexShrink: 0,
                        }}
                    />
                    <Typography sx={{ fontSize: 10.75, color: 'text.secondary' }}>
                        تراکنش موفق
                    </Typography>
                    <Typography
                        sx={{
                            mr: 'auto',
                            fontFamily: `${iranSans} !important`,
                            fontSize: 12.25,
                            fontWeight: 700,
                            color: 'text.primary',
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                        {addCommas(Number(count || 0))} عدد
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

export default GatewatActionCardItem;
